import React, { useState } from "react";
import { WarningIcon, ShieldWarningIcon, InfoIcon } from "@phosphor-icons/react";

const LEVEL_STYLES = {
  clean: {
    pill: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Icon: null,
    label: "Clean",
  },
  caution: {
    pill: "bg-amber-100 text-amber-800 border-amber-300",
    Icon: WarningIcon,
    label: "Caution",
  },
  high: {
    pill: "bg-red-100 text-red-800 border-red-300",
    Icon: ShieldWarningIcon,
    label: "High risk",
  },
};

/**
 * Risk badge with hover/click popover listing every triggered signal.
 * Render inline on each admin row. `risk` shape: {score, level, signals: [{type, severity, message}]}.
 * Hidden entirely when score == 0 (no point clutter for clean rows).
 */
export default function RiskBadge({ risk, testid }) {
  const [open, setOpen] = useState(false);
  if (!risk || !risk.score) return null;

  const style = LEVEL_STYLES[risk.level] || LEVEL_STYLES.caution;
  const { Icon } = style;
  const hasSignals = risk.signals && risk.signals.length > 0;

  return (
    <div className="relative inline-block" data-testid={testid}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${style.pill}`}
        aria-label={`Risk score ${risk.score}: ${style.label}`}
        data-testid={`${testid}-pill`}
      >
        {Icon && <Icon size={11} weight="fill" />}
        <span>Risk {risk.score}</span>
      </button>

      {open && hasSignals && (
        <div
          className="absolute z-30 top-full mt-1 right-0 w-72 bg-white border border-zinc-200 rounded-xl shadow-xl p-3 text-left"
          data-testid={`${testid}-popover`}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
        >
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-700 mb-2">
            <InfoIcon size={13} className="text-zinc-500" />
            {risk.signals.length} signal{risk.signals.length === 1 ? "" : "s"}
          </div>
          <ul className="space-y-1.5">
            {risk.signals.map((s, i) => (
              <li
                key={`${s.type}-${i}`}
                className="text-[12px] leading-snug flex items-start gap-2"
              >
                <span
                  className={`mt-0.5 h-1.5 w-1.5 rounded-full shrink-0 ${
                    s.severity === "high" ? "bg-red-500"
                      : s.severity === "medium" ? "bg-amber-500"
                      : "bg-zinc-400"
                  }`}
                />
                <span className="text-zinc-700">{s.message}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
